import { DayPicker, type DayPickerProps } from '@natera/material/lib/date'
import TextfieldV2 from '@natera/material/lib/textfield/v2'
import { format, isValid, parse } from 'date-fns'
import { useCallback, useEffect, useRef, useState } from 'react'
import { cn } from '~/shared/lib/cn'
import { Popover, PopoverAnchor, PopoverContent } from '~/shared/ui/popover'
import './date-picker.scss'

const MASK_TEMPLATE = 'MM/DD/YYYY'
const DATE_FORMAT = 'MM/dd/yyyy'
// Positions in MASK_TEMPLATE that are digit slots (not slashes)
const DIGIT_POSITIONS = [0, 1, 3, 4, 6, 7, 8, 9]

function digitsToMasked(digits: string): string {
  let result = ''
  let di = 0
  for (let i = 0; i < MASK_TEMPLATE.length; i++) {
    if (MASK_TEMPLATE[i] === '/') {
      result += '/'
    } else if (di < digits.length) {
      result += digits[di++]
    } else {
      result += MASK_TEMPLATE[i]
    }
  }
  return result
}

function maskedCursorPosition(digitCount: number): number {
  if (digitCount >= 8) return 10
  return DIGIT_POSITIONS[digitCount]
}

function dateToDigits(d: Date): string {
  return format(d, DATE_FORMAT).replace(/\//g, '')
}

interface DatePickerProps {
  date?: Date
  defaultDate?: Date
  onDateChange?: (date: Date | undefined) => void
  label?: string
  className?: string
  outline?: boolean
  dense?: boolean
  fullWidth?: boolean
  clearable?: boolean
  disabled?: boolean
  readOnly?: boolean
  help?: boolean
  invalid?: boolean
  helperText?: React.ReactNode
  helperPersistent?: boolean
  minDate?: Date
  maxDate?: Date
  defaultMonthYear?: Date
  required?: boolean
  modifiers?: DayPickerProps['modifiers']
  modifiersStyles?: DayPickerProps['modifiersStyles']
}

export function DatePicker({
  date: controlledDate,
  defaultDate,
  onDateChange,
  label,
  className,
  outline = true,
  dense = true,
  fullWidth = true,
  clearable = false,
  disabled = false,
  readOnly = false,
  help = false,
  invalid,
  helperText,
  helperPersistent,
  minDate,
  maxDate,
  defaultMonthYear,
  required,
  modifiers,
  modifiersStyles,
}: DatePickerProps) {
  const isControlled = onDateChange !== undefined
  const [internalDate, setInternalDate] = useState<Date | undefined>(defaultDate)
  const date = isControlled ? controlledDate : internalDate

  const [open, setOpen] = useState(false)
  const [focused, setFocused] = useState(false)
  // Raw digits the user has typed (no slashes), max 8 chars
  const [digits, setDigits] = useState(() => (date ? dateToDigits(date) : ''))
  const containerRef = useRef<HTMLDivElement>(null)
  const cursorRef = useRef<number | null>(null)

  const getInput = useCallback(
    () => containerRef.current?.querySelector<HTMLInputElement>('input') ?? null,
    [],
  )

  // Sync digits when controlled date changes externally
  useEffect(() => {
    if (isControlled) {
      setDigits(controlledDate ? dateToDigits(controlledDate) : '')
    }
  }, [controlledDate, isControlled])

  // Place cursor after React renders the masked value
  useEffect(() => {
    if (cursorRef.current !== null) {
      const input = getInput()
      if (input) {
        input.setSelectionRange(cursorRef.current, cursorRef.current)
      }
      cursorRef.current = null
    }
  })

  const updateDate = useCallback(
    (newDate: Date | undefined) => {
      if (!isControlled) {
        setInternalDate(newDate)
      }
      onDateChange?.(newDate)
    },
    [isControlled, onDateChange],
  )

  const tryParseAndUpdate = useCallback(
    (rawDigits: string) => {
      if (rawDigits.length === 8) {
        const masked = digitsToMasked(rawDigits)
        const parsed = parse(masked, DATE_FORMAT, new Date())
        if (isValid(parsed)) {
          const outOfBounds = (minDate && parsed < minDate) || (maxDate && parsed > maxDate)
          if (!outOfBounds) {
            updateDate(parsed)
            return
          }
        }
      }
      if (rawDigits.length === 0) {
        updateDate(undefined)
      }
    },
    [updateDate, minDate, maxDate],
  )

  const handleDaySelect = useCallback(
    (newDate: Date | undefined) => {
      updateDate(newDate)
      setDigits(newDate ? dateToDigits(newDate) : '')
      setOpen(false)
      getInput()?.focus()
    },
    [updateDate, getInput],
  )

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      // Extract only new digits from whatever the browser gave us
      const incoming = e.target.value.replace(/\D/g, '')
      const clamped = incoming.slice(0, 8)
      setDigits(clamped)
      cursorRef.current = maskedCursorPosition(clamped.length)
      tryParseAndUpdate(clamped)
    },
    [tryParseAndUpdate],
  )

  const handleInputKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      const input = e.currentTarget

      if (e.key === 'Backspace') {
        e.preventDefault()
        const selStart = input.selectionStart ?? digits.length
        const selEnd = input.selectionEnd ?? selStart

        if (selStart !== selEnd) {
          // Selection: figure out which digit positions are covered
          let firstDigit = 0
          let lastDigit = 0
          let count = 0
          for (let i = 0; i < MASK_TEMPLATE.length && count <= digits.length; i++) {
            if (MASK_TEMPLATE[i] === '/') continue
            if (i < selStart) firstDigit = count + 1
            if (i < selEnd) lastDigit = count + 1
            count++
          }
          const next = digits.slice(0, firstDigit) + digits.slice(lastDigit)
          setDigits(next)
          cursorRef.current = maskedCursorPosition(firstDigit)
          tryParseAndUpdate(next)
        } else if (digits.length > 0) {
          // Find which digit index the cursor is at/before
          let digitIndex = 0
          for (let i = 0; i < MASK_TEMPLATE.length && i < selStart; i++) {
            if (MASK_TEMPLATE[i] !== '/') digitIndex++
          }
          const delIdx = Math.max(0, digitIndex - 1)
          if (delIdx < digits.length) {
            const next = digits.slice(0, delIdx) + digits.slice(delIdx + 1)
            setDigits(next)
            cursorRef.current = maskedCursorPosition(delIdx)
            tryParseAndUpdate(next)
          }
        }
        return
      }

      if (e.key === 'Delete') {
        e.preventDefault()
        const selStart = input.selectionStart ?? 0
        const selEnd = input.selectionEnd ?? selStart

        if (selStart !== selEnd) {
          let firstDigit = 0
          let lastDigit = 0
          let count = 0
          for (let i = 0; i < MASK_TEMPLATE.length && count <= digits.length; i++) {
            if (MASK_TEMPLATE[i] === '/') continue
            if (i < selStart) firstDigit = count + 1
            if (i < selEnd) lastDigit = count + 1
            count++
          }
          const next = digits.slice(0, firstDigit) + digits.slice(lastDigit)
          setDigits(next)
          cursorRef.current = maskedCursorPosition(firstDigit)
          tryParseAndUpdate(next)
        } else {
          // Delete digit at cursor
          let digitIndex = 0
          for (let i = 0; i < MASK_TEMPLATE.length && i < selStart; i++) {
            if (MASK_TEMPLATE[i] !== '/') digitIndex++
          }
          if (digitIndex < digits.length) {
            const next = digits.slice(0, digitIndex) + digits.slice(digitIndex + 1)
            setDigits(next)
            cursorRef.current = maskedCursorPosition(digitIndex)
            tryParseAndUpdate(next)
          }
        }
        return
      }

      if (e.key === 'Enter') {
        if (digits.length === 8) {
          const masked = digitsToMasked(digits)
          const parsed = parse(masked, DATE_FORMAT, new Date())
          if (isValid(parsed)) {
            updateDate(parsed)
            setOpen(false)
          }
        }
        return
      }

      if (e.key === 'Escape') {
        setOpen(false)
      }
    },
    [digits, updateDate, tryParseAndUpdate],
  )

  const handleInputBlur = useCallback(() => {
    setFocused(false)
    if (digits.length === 0) {
      if (date !== undefined) {
        updateDate(undefined)
      }
      return
    }
    if (digits.length === 8) {
      const masked = digitsToMasked(digits)
      const parsed = parse(masked, DATE_FORMAT, new Date())
      if (isValid(parsed)) {
        if (!date || parsed.getTime() !== date.getTime()) {
          updateDate(parsed)
        }
        return
      }
    }
    // Incomplete or invalid — revert to current date
    setDigits(date ? dateToDigits(date) : '')
  }, [digits, date, updateDate])

  const handleClear = useCallback(() => {
    setDigits('')
    updateDate(undefined)
    getInput()?.focus()
  }, [updateDate, getInput])

  const handleFocus = useCallback(() => {
    setFocused(true)
    const pos = maskedCursorPosition(digits.length)
    const input = getInput()
    if (input) {
      requestAnimationFrame(() => {
        input.setSelectionRange(pos, pos)
      })
    }
  }, [digits.length, getInput])

  const handleClick = useCallback(() => {
    if (!disabled && !readOnly) {
      setOpen(true)
    }
  }, [disabled, readOnly])

  const maskedValue = digits.length > 0 ? digitsToMasked(digits) : focused ? MASK_TEMPLATE : ''

  return (
    <Popover
      open={open}
      onOpenChange={setOpen}
    >
      <PopoverAnchor asChild>
        <div
          ref={containerRef}
          className={cn(
            'c-date-picker',
            {
              'c-date-picker--help': help,
              'c-date-picker--read-only': readOnly,
            },
            className,
          )}
        >
          <TextfieldV2
            label={label}
            outline={outline}
            dense={dense}
            fullWidth={fullWidth}
            disabled={disabled || readOnly}
            invalid={invalid}
            helperText={helperText}
            helperPersistent={helperPersistent}
            required={required}
            value={maskedValue}
            onChange={handleInputChange}
            onKeyDown={handleInputKeyDown}
            onFocus={handleFocus}
            onBlur={handleInputBlur}
            clearable={clearable && digits.length > 0}
            onClear={handleClear}
            materialTrailingIcon="event"
            materialClearIcon="close"
            materialLeadIcon={
              help
                ? {
                    children: 'help',
                    fill: true,
                  }
                : undefined
            }
            onClick={handleClick}
          />
        </div>
      </PopoverAnchor>

      <PopoverContent
        align="start"
        sideOffset={0}
        className="c-date-picker__popover"
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        <DayPicker
          date={date}
          onDateChange={handleDaySelect}
          minDate={minDate}
          maxDate={maxDate}
          defaultMonthYear={defaultMonthYear ?? date}
          modifiers={modifiers}
          modifiersStyles={modifiersStyles}
          materialArrowIcon="chevron_right"
          materialDropdownIcon="arrow_drop_down"
        />
      </PopoverContent>
    </Popover>
  )
}
